// src/components/resumo/TableManager.ts
// ✅ v2 — resize bidirecional, paleta ampla, pintar coluna/linha/célula

export interface TableConfig {
    rows: number;
    cols: number;
    hasHeader: boolean;
    zebraStripes: boolean;
    headerBg: string;
    stripeBg: string;
    borderColor: string;
    width: number;
}

const DEFAULT_CONFIG: TableConfig = {
    rows: 3,
    cols: 3,
    hasHeader: true,
    zebraStripes: false,
    headerBg: getComputedStyle(document.body).getPropertyValue('--platform-accent').trim() || '#184C18',
    stripeBg: '#f0f7f0',
    borderColor: '#d1d5db',
    width: 600,
};

const COLOR_PALETTE: { label: string; colors: string[] }[] = [
    {
        label: 'Básicas',
        colors: [
            '#ffffff', '#f9fafb', '#f3f4f6', '#e5e7eb', '#d1d5db',
            '#9ca3af', '#6b7280', '#374151', '#1f2937', '#111827',
        ],
    },
    {
        label: 'Verdes (GRAIA)',
        colors: [
            '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80',
            '#5FBA61', '#2F7130', '#184C18', '#14532d', '#052e16',
        ],
    },
    {
        label: 'Pastéis',
        colors: [
            '#fef9c3', '#fde68a', '#fca5a5', '#fdba74', '#86efac',
            '#a5f3fc', '#bfdbfe', '#c4b5fd', '#f9a8d4', '#fbcfe8',
        ],
    },
    {
        label: 'Azuis',
        colors: [
            '#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa',
            '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a',
        ],
    },
    {
        label: 'Roxos',
        colors: [
            '#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc',
            '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87',
        ],
    },
    {
        label: 'Vermelhos / Rosa',
        colors: [
            '#fff1f2', '#ffe4e6', '#fecdd3', '#fda4af', '#fb7185',
            '#f43f5e', '#e11d48', '#be123c', '#9f1239', '#881337',
        ],
    },
    {
        label: 'Laranjas / Amarelos',
        colors: [
            '#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c',
            '#f97316', '#ea580c', '#fef08a', '#fde047', '#facc15',
        ],
    },
    {
        label: 'Ciano / Teal',
        colors: [
            '#ecfeff', '#cffafe', '#a5f3fc', '#67e8f9', '#22d3ee',
            '#06b6d4', '#0891b2', '#f0fdfa', '#ccfbf1', '#99f6e4',
        ],
    },
];

export class TableManager {
    private editorRef: React.RefObject<HTMLDivElement | null>;
    private onDirty: () => void;

    private activeTable: HTMLElement | null = null;
    private resizeMode: 'width' | 'height' | 'both' | null = null;
    private resizeStartX = 0;
    private resizeStartY = 0;
    private resizeStartWidth = 0;
    private resizeStartHeight = 0;
    private isResizing = false;

    private boundMouseMove: (e: MouseEvent) => void;
    private boundMouseUp: (e: MouseEvent) => void;
    private boundClickOutside: (e: MouseEvent) => void;
    private boundContextMenu: (e: MouseEvent) => void;

    // Seleção múltipla de células (Ctrl+Click)
    private selectedCells: Set<HTMLElement> = new Set();

    constructor(
        editorRef: React.RefObject<HTMLDivElement | null>,
        onDirty: () => void
    ) {
        this.editorRef = editorRef;
        this.onDirty = onDirty;
        this.boundMouseMove = this.handleResizeMove.bind(this);
        this.boundMouseUp = this.handleResizeUp.bind(this);
        this.boundClickOutside = this.handleClickOutside.bind(this);
        this.boundContextMenu = this.handleContextMenu.bind(this);

        document.addEventListener('mousemove', this.boundMouseMove);
        document.addEventListener('mouseup', this.boundMouseUp);
        document.addEventListener('mousedown', this.boundClickOutside);
        document.addEventListener('contextmenu', this.boundContextMenu);
    }

    // ============================================================
    // INSERÇÃO
    // ============================================================

    insertTable(config: Partial<TableConfig> = {}, savedRange: Range | null): void {
        const cfg = { ...DEFAULT_CONFIG, ...config };
        const editor = this.editorRef.current;
        if (!editor) return;

        const html = this.buildTableHTML(cfg);
        editor.focus();

        if (savedRange) {
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(savedRange);
            }
        }

        document.execCommand('insertHTML', false, html);

        setTimeout(() => {
            this.initializeAllTables();
            this.onDirty();
        }, 50);
    }

    private buildTableHTML(cfg: TableConfig): string {
        const id = `tbl_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        const colW = (100 / cfg.cols).toFixed(2);

        let html = `<div class="tbl-wrapper" data-table-id="${id}" style="width:${cfg.width}px; max-width:100%; position:relative; display:inline-block; margin:16px 0;">`;
        html += `<table class="tbl-table" style="border-collapse:collapse; width:100%; table-layout:fixed;"
            data-header-bg="${cfg.headerBg}" data-stripe-bg="${cfg.stripeBg}"
            data-border-color="${cfg.borderColor}" data-has-header="${cfg.hasHeader}" data-zebra="${cfg.zebraStripes}">`;
        html += `<colgroup>`;
        for (let c = 0; c < cfg.cols; c++) {
            html += `<col style="width:${colW}%">`;
        }
        html += `</colgroup><tbody>`;

        for (let r = 0; r < cfg.rows; r++) {
            const isHdr = cfg.hasHeader && r === 0;
            const isEven = r % 2 === 0;
            const rowBg = isHdr
                ? cfg.headerBg
                : (cfg.zebraStripes && isEven ? cfg.stripeBg : 'transparent');
            const rowColor = isHdr ? '#ffffff' : 'inherit';

            html += `<tr data-row="${r}" style="background:${rowBg}; color:${rowColor};">`;
            for (let c = 0; c < cfg.cols; c++) {
                const tag = isHdr ? 'th' : 'td';
                html += `<${tag} data-col="${c}" contenteditable="true"
                    style="border:1px solid ${cfg.borderColor}; padding:8px 12px; vertical-align:top; min-width:40px; word-break:break-word;"
                >${isHdr ? `Coluna ${c + 1}` : ''}</${tag}>`;
            }
            html += `</tr>`;
        }

        html += `</tbody></table>`;
        html += `<div class="tbl-handle tbl-handle-e" data-resize="width" title="Arrastar → largura"></div>`;
        html += `<div class="tbl-handle tbl-handle-s" data-resize="height" title="Arrastar ↓ altura"></div>`;
        html += `<div class="tbl-handle tbl-handle-se" data-resize="both" title="Arrastar ↘ largura e altura"></div>`;
        html += `</div><p><br></p>`;

        return html;
    }

    // ============================================================
    // INICIALIZAÇÃO — sempre re-anexa, sem flag de bloqueio
    // ============================================================

    initializeAllTables(): void {
        const editor = this.editorRef.current;
        if (!editor) return;

        editor.querySelectorAll<HTMLElement>('.tbl-wrapper').forEach(wrapper => {
            this.attachHandles(wrapper);
            this.attachCellHandlers(wrapper);
        });
    }

    private attachHandles(wrapper: HTMLElement): void {
        wrapper.querySelectorAll<HTMLElement>('.tbl-handle').forEach(handle => {
            // Clonar para limpar todos os listeners antigos garantidamente
            const clone = handle.cloneNode(true) as HTMLElement;
            handle.replaceWith(clone);

            clone.addEventListener('mousedown', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                this.isResizing = true;
                this.activeTable = wrapper;
                this.resizeMode = (clone.dataset.resize as 'width' | 'height' | 'both') || 'both';
                this.resizeStartX = e.clientX;
                this.resizeStartY = e.clientY;
                this.resizeStartWidth = wrapper.offsetWidth;

                const table = wrapper.querySelector('table');
                this.resizeStartHeight = table ? table.offsetHeight : wrapper.offsetHeight;

                wrapper.classList.add('tbl-resizing');
                document.body.style.userSelect = 'none';

                if (this.resizeMode === 'width') document.body.style.cursor = 'ew-resize';
                else if (this.resizeMode === 'height') document.body.style.cursor = 'ns-resize';
                else document.body.style.cursor = 'nwse-resize';
            });
        });
    }

    private attachCellHandlers(wrapper: HTMLElement): void {
        wrapper.querySelectorAll<HTMLElement>('td, th').forEach(cell => {
            cell.onclick = (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.tbl-handle, .tbl-col-handle, .tbl-row-handle')) return;
                this.selectTable(wrapper);

                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    if (this.selectedCells.has(cell)) {
                        this.selectedCells.delete(cell);
                        cell.classList.remove('tbl-cell-selected');
                    } else {
                        this.selectedCells.add(cell);
                        cell.classList.add('tbl-cell-selected');
                    }
                } else {
                    // Limpar seleção anterior ao clicar sem Ctrl
                    this.selectedCells.forEach(c => c.classList.remove('tbl-cell-selected'));
                    this.selectedCells.clear();
                }
            };
        });

        // ✅ NOVO: handle de resize por linha
        wrapper.querySelectorAll<HTMLElement>('tr').forEach((row) => {
            // Verificar se já tem handle
            if (row.querySelector('.tbl-row-handle')) return;

            const rowHandle = document.createElement('div');
            rowHandle.className = 'tbl-row-handle';
            rowHandle.title = 'Arrastar para ajustar altura desta linha';

            let startY = 0;
            let startH = 0;

            rowHandle.addEventListener('mousedown', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                startY = e.clientY;
                startH = row.offsetHeight;
                document.body.style.cursor = 'ns-resize';
                document.body.style.userSelect = 'none';

                const onMove = (ev: MouseEvent) => {
                    const newH = Math.max(28, startH + (ev.clientY - startY));
                    row.style.height = `${newH}px`;
                    row.querySelectorAll<HTMLElement>('td, th').forEach(cell => {
                        cell.style.height = `${newH}px`;
                    });
                };

                const onUp = () => {
                    document.body.style.cursor = '';
                    document.body.style.userSelect = '';
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    this.onDirty();
                };

                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            });

            // Inserir o handle como último filho do primeiro td/th da linha
            row.style.position = 'relative';
            row.appendChild(rowHandle);
        });

        // Handles de resize de coluna (primeira linha, exceto última célula)
        const firstRow = wrapper.querySelector('tr');
        if (firstRow) {
            // Remove handles antigos para re-criar (necessário após inserir/remover colunas)
            firstRow.querySelectorAll('.tbl-col-handle').forEach(h => h.remove());

            const cells = Array.from(firstRow.querySelectorAll<HTMLElement>('td, th'));
            cells.forEach((cell, idx) => {
                if (idx === cells.length - 1) return; // última coluna: sem handle

                cell.style.position = 'relative';

                const colHandle = document.createElement('div');
                colHandle.className = 'tbl-col-handle';
                colHandle.title = 'Arrastar para ajustar largura desta coluna';
                cell.appendChild(colHandle);

                colHandle.addEventListener('mousedown', (e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const colIdx = parseInt(cell.dataset.col ?? String(idx));
                    const colgroup = wrapper.querySelector('colgroup');
                    const tableEl = wrapper.querySelector<HTMLElement>('table');
                    if (!colgroup || !tableEl) return;

                    const cols = Array.from(colgroup.querySelectorAll('col'));
                    const thisCol = cols[colIdx];
                    const nextCol = cols[colIdx + 1];
                    if (!thisCol || !nextCol) return;

                    const tableWidth = tableEl.offsetWidth;
                    const startX = e.clientX;
                    const minPx = 40;

                    const getColPx = (col: Element): number => {
                        const m = (col.getAttribute('style') || '').match(/width:\s*([\d.]+)%/);
                        return m ? (parseFloat(m[1]) / 100) * tableWidth : tableWidth / cols.length;
                    };

                    const startThisPx = getColPx(thisCol);
                    const startNextPx = getColPx(nextCol);

                    document.body.style.cursor = 'col-resize';
                    document.body.style.userSelect = 'none';

                    const onMove = (ev: MouseEvent) => {
                        const dx = ev.clientX - startX;
                        const clampedDx = Math.max(-(startThisPx - minPx), Math.min(startNextPx - minPx, dx));
                        const newThisPx = startThisPx + clampedDx;
                        const newNextPx = startNextPx - clampedDx;
                        thisCol.setAttribute('style', `width:${(newThisPx / tableWidth * 100).toFixed(2)}%`);
                        nextCol.setAttribute('style', `width:${(newNextPx / tableWidth * 100).toFixed(2)}%`);
                    };

                    const onUp = () => {
                        document.body.style.cursor = '';
                        document.body.style.userSelect = '';
                        document.removeEventListener('mousemove', onMove);
                        document.removeEventListener('mouseup', onUp);
                        this.onDirty();
                    };

                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                });
            });
        }
    }

    private selectTable(wrapper: HTMLElement): void {
        document.querySelectorAll('.tbl-wrapper.tbl-selected').forEach(el =>
            el.classList.remove('tbl-selected')
        );
        wrapper.classList.add('tbl-selected');
        this.activeTable = wrapper;
    }

    // ============================================================
    // RESIZE BIDIRECIONAL
    // ============================================================

    private handleResizeMove(e: MouseEvent): void {
        if (!this.isResizing || !this.activeTable || !this.resizeMode) return;

        const dx = e.clientX - this.resizeStartX;
        const dy = e.clientY - this.resizeStartY;

        if (this.resizeMode === 'width' || this.resizeMode === 'both') {
            const newW = Math.max(200, this.resizeStartWidth + dx);
            this.activeTable.style.width = `${newW}px`;
        }

        if (this.resizeMode === 'height' || this.resizeMode === 'both') {
            const rows = this.activeTable.querySelectorAll<HTMLElement>('tr');
            if (rows.length > 0) {
                const newH = Math.max(60, this.resizeStartHeight + dy);
                const rowH = Math.max(30, Math.floor(newH / rows.length));
                rows.forEach(row => {
                    row.style.height = `${rowH}px`;
                    row.querySelectorAll<HTMLElement>('td, th').forEach(cell => {
                        cell.style.height = `${rowH}px`;
                    });
                });
            }
        }
    }

    private handleResizeUp(): void {
        if (!this.isResizing) return;
        this.isResizing = false;
        this.resizeMode = null;
        if (this.activeTable) {
            this.activeTable.classList.remove('tbl-resizing');
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        this.onDirty();
    }

    // ============================================================
    // CONTEXT MENU
    // ============================================================

    private handleContextMenu(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        const cell = target.closest('td, th') as HTMLElement | null;
        const wrapper = target.closest('.tbl-wrapper') as HTMLElement | null;
        if (!cell || !wrapper) return;

        e.preventDefault();
        this.showContextMenu(e.clientX, e.clientY, cell, wrapper);
    }

    private showContextMenu(x: number, y: number, cell: HTMLElement, wrapper: HTMLElement): void {
        this.removeContextMenu();

        const row = cell.closest('tr') as HTMLElement;
        const rowIndex = parseInt(row.dataset.row || '0');
        const colIndex = parseInt(cell.dataset.col || '0');

        const menu = document.createElement('div');
        menu.className = 'tbl-context-menu';
        menu.style.cssText = `position:fixed; left:${x}px; top:${y}px; z-index:99999;`;

        const isMerged = parseInt(cell.getAttribute('colspan') || '1') > 1
            || parseInt(cell.getAttribute('rowspan') || '1') > 1;

        // Detectar células selecionadas na mesma linha (para merge horizontal)
        const selectedInRow = Array.from(this.selectedCells).filter(c =>
            c.closest('tr') === row && wrapper.contains(c)
        );
        // Detectar células selecionadas na mesma coluna (para merge vertical)
        const selectedInCol = Array.from(this.selectedCells).filter(c =>
            c.dataset.col === cell.dataset.col && wrapper.contains(c)
        );

        const canMergeH = selectedInRow.length >= 2 || (!this.selectedCells.size && false);
        const canMergeV = selectedInCol.length >= 2;

        const items: { label: string; action: (() => void) | null; danger?: boolean }[] = [
            { label: 'Cor desta célula', action: () => this.paintCell(cell) },
            { label: 'Cor desta linha', action: () => this.paintRow(row) },
            { label: 'Cor desta coluna', action: () => this.paintColumn(colIndex, wrapper) },
            { label: '─', action: null },
            ...(canMergeH ? [{ label: `Unir ${selectedInRow.length} colunas selecionadas`, action: () => this.mergeCellsHorizontal(selectedInRow, wrapper) }] : []),
            ...(canMergeV ? [{ label: `Unir ${selectedInCol.length} linhas selecionadas`, action: () => this.mergeCellsVertical(selectedInCol, cell.dataset.col || '0', wrapper) }] : []),
            ...(isMerged ? [{ label: 'Separar células (unmerge)', action: () => this.unmergeCell(cell, wrapper) }] : []),
            ...((canMergeH || canMergeV || isMerged) ? [{ label: '─', action: null }] : []),
            { label: 'Inserir linha acima', action: () => this.insertRow(wrapper, rowIndex, 'before') },
            { label: 'Inserir linha abaixo', action: () => this.insertRow(wrapper, rowIndex, 'after') },
            { label: 'Inserir coluna antes', action: () => this.insertCol(wrapper, colIndex, 'before') },
            { label: 'Inserir coluna depois', action: () => this.insertCol(wrapper, colIndex, 'after') },
            { label: '─', action: null },
            { label: 'Excluir linha', action: () => this.deleteRow(wrapper, rowIndex), danger: true },
            { label: 'Excluir coluna', action: () => this.deleteCol(wrapper, colIndex), danger: true },
            { label: '─', action: null },
            { label: 'Toggle zebra stripes', action: () => this.toggleZebra(wrapper) },
            { label: 'Excluir tabela', action: () => this.deleteTable(wrapper), danger: true },
            { label: '─', action: null },
            { label: 'Alinhar esquerda', action: () => this.alignTable(wrapper, 'left') },
            { label: 'Centralizar', action: () => this.alignTable(wrapper, 'center') },
            { label: 'Alinhar direita', action: () => this.alignTable(wrapper, 'right') },
        ];

        items.forEach(item => {
            if (item.label === '─') {
                const sep = document.createElement('div');
                sep.className = 'tbl-ctx-sep';
                menu.appendChild(sep);
                return;
            }
            const btn = document.createElement('button');
            btn.className = 'tbl-ctx-item';
            btn.textContent = item.label;
            if (item.danger || item.label.startsWith('Excluir')) {
                btn.dataset.danger = 'true';
            }
            btn.onclick = () => { this.removeContextMenu(); item.action?.(); };
            menu.appendChild(btn);
        });

        document.body.appendChild(menu);

        const rect = menu.getBoundingClientRect();
        if (rect.right > window.innerWidth) menu.style.left = `${x - rect.width}px`;
        if (rect.bottom > window.innerHeight) menu.style.top = `${y - rect.height}px`;

        setTimeout(() => {
            document.addEventListener('click', () => this.removeContextMenu(), { once: true });
        }, 10);
    }

    private removeContextMenu(): void {
        document.querySelectorAll('.tbl-context-menu').forEach(el => el.remove());
    }

    // ============================================================
    // PINTAR
    // ============================================================

    private paintCell(cell: HTMLElement): void {
        this.showPalette((color) => {
            cell.style.backgroundColor = color === 'transparent' ? '' : color;
            this.onDirty();
        });
    }

    private paintRow(row: HTMLElement): void {
        this.showPalette((color) => {
            const bg = color === 'transparent' ? '' : color;
            row.style.backgroundColor = bg;
            row.style.color = bg ? this.contrastColor(color) : '';
            row.querySelectorAll<HTMLElement>('td, th').forEach(cell => {
                cell.style.backgroundColor = '';
            });
            this.onDirty();
        });
    }

    private paintColumn(colIndex: number, wrapper: HTMLElement): void {
        this.showPalette((color) => {
            const bg = color === 'transparent' ? '' : color;
            wrapper.querySelectorAll<HTMLElement>(
                `td[data-col="${colIndex}"], th[data-col="${colIndex}"]`
            ).forEach(cell => {
                cell.style.backgroundColor = bg;
                cell.style.color = bg ? this.contrastColor(color) : '';
            });
            this.onDirty();
        });
    }

    // ============================================================
    // PALETA DE CORES
    // ============================================================

    private showPalette(onSelect: (color: string) => void): void {
        document.querySelector('.tbl-palette-popup')?.remove();

        const popup = document.createElement('div');
        popup.className = 'tbl-palette-popup';

        const title = document.createElement('div');
        title.className = 'tbl-palette-title';
        title.textContent = 'Escolha uma cor';
        popup.appendChild(title);

        COLOR_PALETTE.forEach(group => {
            const label = document.createElement('div');
            label.className = 'tbl-palette-group-label';
            label.textContent = group.label;
            popup.appendChild(label);

            const grid = document.createElement('div');
            grid.className = 'tbl-palette-grid';

            group.colors.forEach(color => {
                const swatch = document.createElement('button');
                swatch.className = 'tbl-palette-swatch';
                swatch.style.background = color;
                if (color === '#ffffff') swatch.style.border = '1px solid #d1d5db';
                swatch.title = color;
                swatch.onclick = (e) => {
                    e.stopPropagation();
                    popup.remove();
                    onSelect(color);
                };
                grid.appendChild(swatch);
            });

            popup.appendChild(grid);
        });

        const sep = document.createElement('div');
        sep.className = 'tbl-palette-sep';
        popup.appendChild(sep);

        const customRow = document.createElement('div');
        customRow.className = 'tbl-palette-custom-row';

        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.value = '#2F7130';
        colorInput.className = 'tbl-palette-color-input';

        const applyBtn = document.createElement('button');
        applyBtn.className = 'tbl-ctx-item';
        applyBtn.textContent = '✓ Cor personalizada';
        applyBtn.style.flex = '1';
        applyBtn.onclick = () => { popup.remove(); onSelect(colorInput.value); };

        customRow.appendChild(colorInput);
        customRow.appendChild(applyBtn);
        popup.appendChild(customRow);

        const clearBtn = document.createElement('button');
        clearBtn.className = 'tbl-ctx-item tbl-ctx-clear';
        clearBtn.textContent = '✕ Remover cor';
        clearBtn.onclick = () => { popup.remove(); onSelect('transparent'); };
        popup.appendChild(clearBtn);

        popup.style.cssText = 'position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); z-index:999999;';
        document.body.appendChild(popup);

        setTimeout(() => {
            const handler = (ev: MouseEvent) => {
                if (!popup.contains(ev.target as Node)) {
                    popup.remove();
                    document.removeEventListener('mousedown', handler);
                }
            };
            document.addEventListener('mousedown', handler);
        }, 10);
    }

    private contrastColor(hex: string): string {
        if (!hex || hex === 'transparent') return 'inherit';
        const c = hex.replace('#', '');
        if (c.length !== 6) return 'inherit';
        const r = parseInt(c.substr(0, 2), 16);
        const g = parseInt(c.substr(2, 2), 16);
        const b = parseInt(c.substr(4, 2), 16);
        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return lum > 0.5 ? '#111827' : '#ffffff';
    }

    // ============================================================
    // ESTRUTURA
    // ============================================================

    private insertRow(wrapper: HTMLElement, rowIndex: number, position: 'before' | 'after'): void {
        const tbody = wrapper.querySelector('tbody');
        if (!tbody) return;

        const rows = tbody.querySelectorAll('tr');
        const refRow = rows[rowIndex] as HTMLElement;
        if (!refRow) return;

        const colCount = refRow.querySelectorAll('td, th').length;
        const borderColor = wrapper.querySelector('table')?.dataset.borderColor || '#d1d5db';

        const newRow = document.createElement('tr');
        newRow.style.background = 'transparent';

        for (let c = 0; c < colCount; c++) {
            const td = document.createElement('td');
            td.dataset.col = String(c);
            td.contentEditable = 'true';
            td.style.cssText = `border:1px solid ${borderColor}; padding:8px 12px; vertical-align:top; min-width:40px; word-break:break-word;`;
            newRow.appendChild(td);
        }

        if (position === 'before') {
            tbody.insertBefore(newRow, refRow);
        } else {
            refRow.nextSibling
                ? tbody.insertBefore(newRow, refRow.nextSibling)
                : tbody.appendChild(newRow);
        }

        this.reIndexRows(wrapper);
        this.attachCellHandlers(wrapper);
        this.onDirty();
    }

    private insertCol(wrapper: HTMLElement, colIndex: number, position: 'before' | 'after'): void {
        const table = wrapper.querySelector('table');
        const colgroup = wrapper.querySelector('colgroup');
        if (!table || !colgroup) return;

        const cols = colgroup.querySelectorAll('col');
        const totalCols = cols.length + 1;
        const newColW = `${(100 / totalCols).toFixed(2)}%`;

        cols.forEach(col => col.setAttribute('style', `width:${newColW}`));

        const newCol = document.createElement('col');
        newCol.setAttribute('style', `width:${newColW}`);
        const refCol = cols[colIndex] as Element;

        if (position === 'before') {
            colgroup.insertBefore(newCol, refCol);
        } else {
            refCol.nextSibling
                ? colgroup.insertBefore(newCol, refCol.nextSibling)
                : colgroup.appendChild(newCol);
        }

        const borderColor = table.dataset.borderColor || '#d1d5db';

        table.querySelectorAll('tr').forEach((row, ri) => {
            const cells = row.querySelectorAll('td, th');
            const refCell = cells[colIndex] as HTMLElement;
            const isHdr = ri === 0 && table.dataset.hasHeader === 'true';
            const tag = isHdr ? 'th' : 'td';

            const newCell = document.createElement(tag);
            newCell.contentEditable = 'true';
            newCell.style.cssText = `border:1px solid ${borderColor}; padding:8px 12px; vertical-align:top; min-width:40px; word-break:break-word;`;

            if (position === 'before') {
                row.insertBefore(newCell, refCell);
            } else {
                refCell.nextSibling
                    ? row.insertBefore(newCell, refCell.nextSibling)
                    : row.appendChild(newCell);
            }
        });

        this.reIndexCols(wrapper);
        this.attachCellHandlers(wrapper);
        this.onDirty();
    }

    private deleteRow(wrapper: HTMLElement, rowIndex: number): void {
        const tbody = wrapper.querySelector('tbody');
        if (!tbody) return;
        const rows = tbody.querySelectorAll('tr');
        if (rows.length <= 1) return;
        rows[rowIndex]?.remove();
        this.reIndexRows(wrapper);
        this.onDirty();
    }

    private deleteCol(wrapper: HTMLElement, colIndex: number): void {
        const colgroup = wrapper.querySelector('colgroup');
        const cols = colgroup?.querySelectorAll('col');
        if (!cols || cols.length <= 1) return;

        cols[colIndex]?.remove();
        const remaining = colgroup!.querySelectorAll('col');
        const newW = `${(100 / remaining.length).toFixed(2)}%`;
        remaining.forEach(col => col.setAttribute('style', `width:${newW}`));

        wrapper.querySelectorAll('tr').forEach(row => {
            row.querySelectorAll('td, th')[colIndex]?.remove();
        });

        this.reIndexCols(wrapper);
        this.onDirty();
    }

    private deleteTable(wrapper: HTMLElement): void {
        wrapper.remove();
        this.activeTable = null;
        this.onDirty();
    }

    private toggleZebra(wrapper: HTMLElement): void {
        const table = wrapper.querySelector('table');
        if (!table) return;

        const isZebra = table.dataset.zebra === 'true';
        table.dataset.zebra = isZebra ? 'false' : 'true';
        table.classList.toggle('tbl-zebra', !isZebra);

        const stripeBg = table.dataset.stripeBg || '#f0f7f0';
        const hasHeader = table.dataset.hasHeader === 'true';

        table.querySelectorAll('tr').forEach((row, i) => {
            const rowEl = row as HTMLElement;
            const isHeaderRow = hasHeader && i === 0;
            if (isHeaderRow) return;
            rowEl.style.backgroundColor = (!isZebra && i % 2 === 0) ? stripeBg : 'transparent';
        });

        this.onDirty();
    }

    private reIndexRows(wrapper: HTMLElement): void {
        wrapper.querySelectorAll('tr').forEach((row, i) => {
            (row as HTMLElement).dataset.row = String(i);
        });
    }

    private reIndexCols(wrapper: HTMLElement): void {
        wrapper.querySelectorAll('tr').forEach(row => {
            row.querySelectorAll('td, th').forEach((cell, i) => {
                (cell as HTMLElement).dataset.col = String(i);
            });
        });
    }

    // ============================================================
    // CLICK OUTSIDE
    // ============================================================

    private handleClickOutside(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        const inWrapper = !!target.closest('.tbl-wrapper');
        const inMenu = !!target.closest('.tbl-context-menu');
        const inPalette = !!target.closest('.tbl-palette-popup');

        if (!inWrapper && !inMenu && !inPalette) {
            document.querySelectorAll('.tbl-wrapper.tbl-selected').forEach(el =>
                el.classList.remove('tbl-selected')
            );
            this.removeContextMenu();
        }
    }

    // ============================================================
    // MERGE / UNMERGE
    // ============================================================

    private mergeCellsHorizontal(cells: HTMLElement[], wrapper: HTMLElement): void {
        if (cells.length < 2) return;

        // Ordenar por coluna
        const sorted = [...cells].sort((a, b) =>
            parseInt(a.dataset.col || '0') - parseInt(b.dataset.col || '0')
        );

        // Coletar conteúdo
        const combined = sorted.map(c => c.innerHTML.trim()).filter(Boolean).join(' ');
        const totalSpan = sorted.reduce((acc, c) =>
            acc + parseInt(c.getAttribute('colspan') || '1'), 0
        );

        const first = sorted[0];
        first.setAttribute('colspan', String(totalSpan));
        first.innerHTML = combined || '';
        first.classList.remove('tbl-cell-selected');

        // Remover as outras
        sorted.slice(1).forEach(c => {
            c.classList.remove('tbl-cell-selected');
            c.remove();
        });

        this.selectedCells.clear();
        this.reIndexCols(wrapper);
        this.attachCellHandlers(wrapper);
        this.onDirty();
    }

    private mergeCellsVertical(cells: HTMLElement[], colIdx: string, wrapper: HTMLElement): void {
        if (cells.length < 2) return;

        // Ordenar por linha
        const sorted = [...cells].sort((a, b) => {
            const rowA = parseInt((a.closest('tr') as HTMLElement)?.dataset.row || '0');
            const rowB = parseInt((b.closest('tr') as HTMLElement)?.dataset.row || '0');
            return rowA - rowB;
        });

        const combined = sorted.map(c => c.innerHTML.trim()).filter(Boolean).join('<br>');
        const totalSpan = sorted.reduce((acc, c) =>
            acc + parseInt(c.getAttribute('rowspan') || '1'), 0
        );

        const first = sorted[0];
        first.setAttribute('rowspan', String(totalSpan));
        first.innerHTML = combined || '';
        first.classList.remove('tbl-cell-selected');

        sorted.slice(1).forEach(c => {
            c.classList.remove('tbl-cell-selected');
            c.remove();
        });

        this.selectedCells.clear();
        this.attachCellHandlers(wrapper);
        this.onDirty();
    }

    private unmergeCell(cell: HTMLElement, wrapper: HTMLElement): void {
        const colspan = parseInt(cell.getAttribute('colspan') || '1');
        const rowspan = parseInt(cell.getAttribute('rowspan') || '1');

        if (colspan <= 1 && rowspan <= 1) return;

        const row = cell.closest('tr') as HTMLElement;
        const borderColor = wrapper.querySelector('table')?.dataset.borderColor || '#d1d5db';
        const colIdx = parseInt(cell.dataset.col || '0');

        if (colspan > 1) {
            cell.removeAttribute('colspan');
            for (let i = 1; i < colspan; i++) {
                const newCell = document.createElement(cell.tagName.toLowerCase() as 'td' | 'th');
                newCell.contentEditable = 'true';
                newCell.style.cssText = cell.style.cssText;
                newCell.style.cssText = `border:1px solid ${borderColor}; padding:8px 12px; vertical-align:top; min-width:40px; word-break:break-word;`;
                cell.after(newCell);
            }
        }

        if (rowspan > 1) {
            cell.removeAttribute('rowspan');
            const tbody = wrapper.querySelector('tbody');
            if (tbody) {
                const rows = Array.from(tbody.querySelectorAll('tr')) as HTMLElement[];
                const cellRowIdx = rows.indexOf(row);
                for (let i = 1; i < rowspan; i++) {
                    const targetRow = rows[cellRowIdx + i];
                    if (!targetRow) continue;
                    const newCell = document.createElement(cell.tagName.toLowerCase() as 'td' | 'th');
                    newCell.contentEditable = 'true';
                    newCell.style.cssText = `border:1px solid ${borderColor}; padding:8px 12px; vertical-align:top; min-width:40px; word-break:break-word;`;
                    const refCell = targetRow.querySelectorAll('td, th')[colIdx];
                    if (refCell) {
                        targetRow.insertBefore(newCell, refCell);
                    } else {
                        targetRow.appendChild(newCell);
                    }
                }
            }
        }

        this.reIndexCols(wrapper);
        this.attachCellHandlers(wrapper);
        this.onDirty();
    }

    private alignTable(wrapper: HTMLElement, align: 'left' | 'center' | 'right'): void {
        if (align === 'left') {
            wrapper.style.marginLeft = '0';
            wrapper.style.marginRight = 'auto';
            wrapper.style.display = 'block';
        } else if (align === 'center') {
            wrapper.style.marginLeft = 'auto';
            wrapper.style.marginRight = 'auto';
            wrapper.style.display = 'block';
        } else {
            wrapper.style.marginLeft = 'auto';
            wrapper.style.marginRight = '0';
            wrapper.style.display = 'block';
        }
        this.onDirty();
    }

    // ============================================================
    // CLEANUP
    // ============================================================

    cleanup(): void {
        document.removeEventListener('mousemove', this.boundMouseMove);
        document.removeEventListener('mouseup', this.boundMouseUp);
        document.removeEventListener('mousedown', this.boundClickOutside);
        document.removeEventListener('contextmenu', this.boundContextMenu);
        this.removeContextMenu();
        document.querySelector('.tbl-palette-popup')?.remove();
    }
}